// src/services/dynamicQuestionService.js
// Dynamic Parametric Question Engine for Mathematics Assessment
// Phase 1-3: Interval Model, Predicate DSL, Graph Feature Extractor

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 1: INTERVAL CLASS + OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Represents a mathematical interval with open/closed boundaries
 * Supports infinity and provides comprehensive interval operations
 */
export class Interval {
  constructor(from, to, fromInclusive = false, toInclusive = false) {
    this.from = from;
    this.to = to;
    this.fromInclusive = fromInclusive;
    this.toInclusive = toInclusive;
  }

  // Factory methods for common interval types
  static open(from, to) {
    return new Interval(from, to, false, false);
  }

  static closed(from, to) {
    return new Interval(from, to, true, true);
  }

  static leftClosed(from, to) {
    return new Interval(from, to, true, false);
  }

  static rightClosed(from, to) {
    return new Interval(from, to, false, true);
  }

  static point(x) {
    return new Interval(x, x, true, true);
  }

  static all() {
    return new Interval(-Infinity, Infinity, false, false);
  }

  static empty() {
    return new Interval(0, 0, false, false, true);
  }

  // Check if interval is empty
  isEmpty() {
    if (this._isEmpty) return true;
    if (this.from > this.to) return true;
    if (this.from === this.to && (!this.fromInclusive || !this.toInclusive)) return true;
    return false;
  }

  // Check if a point is contained in the interval
  contains(x) {
    if (this.isEmpty()) return false;
    
    const leftOk = this.fromInclusive ? (x >= this.from) : (x > this.from);
    const rightOk = this.toInclusive ? (x <= this.to) : (x < this.to);
    
    return leftOk && rightOk;
  }

  // Check if this interval is a subset of another
  isSubsetOf(other) {
    if (this.isEmpty()) return true;
    if (other.isEmpty()) return false;

    // Check left boundary
    let leftOk = false;
    if (this.from > other.from) {
      leftOk = true;
    } else if (this.from === other.from) {
      leftOk = other.fromInclusive || !this.fromInclusive;
    }

    // Check right boundary
    let rightOk = false;
    if (this.to < other.to) {
      rightOk = true;
    } else if (this.to === other.to) {
      rightOk = other.toInclusive || !this.toInclusive;
    }

    return leftOk && rightOk;
  }

  // Check if this interval overlaps with another
  overlaps(other) {
    if (this.isEmpty() || other.isEmpty()) return false;

    // Check if intervals are disjoint
    if (this.to < other.from || other.to < this.from) return false;
    
    // Check boundary cases
    if (this.to === other.from) {
      return this.toInclusive && other.fromInclusive;
    }
    if (other.to === this.from) {
      return other.toInclusive && this.fromInclusive;
    }

    return true;
  }

  // Compute intersection of two intervals
  intersection(other) {
    if (!this.overlaps(other)) return Interval.empty();

    const from = Math.max(this.from, other.from);
    const to = Math.min(this.to, other.to);

    let fromInclusive;
    if (this.from === other.from) {
      fromInclusive = this.fromInclusive && other.fromInclusive;
    } else {
      fromInclusive = this.from > other.from ? this.fromInclusive : other.fromInclusive;
    }

    let toInclusive;
    if (this.to === other.to) {
      toInclusive = this.toInclusive && other.toInclusive;
    } else {
      toInclusive = this.to < other.to ? this.toInclusive : other.toInclusive;
    }

    return new Interval(from, to, fromInclusive, toInclusive);
  }

  // Get width of interval
  width() {
    if (this.isEmpty()) return 0;
    if (!isFinite(this.from) || !isFinite(this.to)) return Infinity;
    return this.to - this.from;
  }

  // Get midpoint of interval
  midpoint() {
    if (this.isEmpty()) return NaN;
    if (!isFinite(this.from) && !isFinite(this.to)) return 0;
    if (!isFinite(this.from)) return this.to - 1;
    if (!isFinite(this.to)) return this.from + 1;
    return (this.from + this.to) / 2;
  }

  // Convert to display string
  toString(notation = 'interval') {
    if (this.isEmpty()) return '∅';

    if (notation === 'inequality') {
      return this.toInequality('x');
    }

    const leftBracket = this.fromInclusive ? '[' : '(';
    const rightBracket = this.toInclusive ? ']' : ')';
    const fromStr = this.from === -Infinity ? '-∞' : this.from;
    const toStr = this.to === Infinity ? '∞' : this.to;

    return `${leftBracket}${fromStr}, ${toStr}${rightBracket}`;
  }

  // Convert to inequality notation
  toInequality(variable = 'x') {
    if (this.isEmpty()) return 'false';

    if (this.from === -Infinity && this.to === Infinity) {
      return `${variable} ∈ ℝ`;
    }

    if (this.from === -Infinity) {
      const op = this.toInclusive ? '≤' : '<';
      return `${variable} ${op} ${this.to}`;
    }

    if (this.to === Infinity) {
      const op = this.fromInclusive ? '≥' : '>';
      return `${variable} ${op} ${this.from}`;
    }

    const leftOp = this.fromInclusive ? '≤' : '<';
    const rightOp = this.toInclusive ? '≤' : '<';
    return `${this.from} ${leftOp} ${variable} ${rightOp} ${this.to}`;
  }

  // Serialize to JSON-safe object
  toJSON() {
    return {
      from: this.from,
      to: this.to,
      fromInclusive: this.fromInclusive,
      toInclusive: this.toInclusive
    };
  }

  // Create from JSON object
  static fromJSON(obj) {
    return new Interval(obj.from, obj.to, obj.fromInclusive, obj.toInclusive);
  }

  // Clone interval
  clone() {
    return new Interval(this.from, this.to, this.fromInclusive, this.toInclusive);
  }
}

/**
 * Collection of intervals with set operations
 */
export class IntervalSet {
  constructor(intervals = []) {
    this.intervals = intervals.map(i => i instanceof Interval ? i : Interval.fromJSON(i));
    this._normalize();
  }

  // Normalize: sort and merge overlapping intervals
  _normalize() {
    if (this.intervals.length <= 1) return;

    // Sort by start point
    this.intervals.sort((a, b) => {
      if (a.from !== b.from) return a.from - b.from;
      return (b.fromInclusive ? 1 : 0) - (a.fromInclusive ? 1 : 0);
    });

    // Merge overlapping intervals
    const merged = [this.intervals[0]];
    for (let i = 1; i < this.intervals.length; i++) {
      const current = this.intervals[i];
      const last = merged[merged.length - 1];

      if (last.to > current.from || 
          (last.to === current.from && (last.toInclusive || current.fromInclusive))) {
        // Merge intervals
        const newTo = Math.max(last.to, current.to);
        let newToInclusive;
        if (last.to === current.to) {
          newToInclusive = last.toInclusive || current.toInclusive;
        } else {
          newToInclusive = last.to > current.to ? last.toInclusive : current.toInclusive;
        }
        merged[merged.length - 1] = new Interval(
          last.from, newTo, last.fromInclusive, newToInclusive
        );
      } else {
        merged.push(current);
      }
    }

    this.intervals = merged.filter(i => !i.isEmpty());
  }

  // Check if a point is in any interval
  contains(x) {
    return this.intervals.some(i => i.contains(x));
  }

  // Check if an interval is a subset of this set
  containsInterval(interval) {
    if (interval.isEmpty()) return true;
    return this.intervals.some(i => interval.isSubsetOf(i));
  }

  // Intersect with a single interval (apply domain restriction)
  intersectWith(interval) {
    const newIntervals = this.intervals
      .map(i => i.intersection(interval))
      .filter(i => !i.isEmpty());
    return new IntervalSet(newIntervals);
  }

  // Union with another IntervalSet
  union(other) {
    return new IntervalSet([...this.intervals, ...other.intervals]);
  }

  // Get total measure (sum of widths)
  measure() {
    return this.intervals.reduce((sum, i) => sum + i.width(), 0);
  }

  // Check if empty
  isEmpty() {
    return this.intervals.length === 0;
  }

  // Convert to string
  toString(notation = 'interval') {
    if (this.isEmpty()) return '∅';
    return this.intervals.map(i => i.toString(notation)).join(' ∪ ');
  }

  // Serialize
  toJSON() {
    return this.intervals.map(i => i.toJSON());
  }

  // Create from JSON
  static fromJSON(arr) {
    return new IntervalSet(arr.map(obj => Interval.fromJSON(obj)));
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 2: PREDICATE DSL EVALUATOR
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Safe predicate evaluator using structured DSL
 * No string eval - pure structured evaluation
 */
export class PredicateEvaluator {
  constructor(context = {}) {
    this.context = context;
  }

  /**
   * Resolve a path like "features.vertex.x" or "params.a" from context
   * Returns string literals as-is if they don't resolve to a path
   */
  resolvePath(path) {
    if (typeof path !== 'string') return path;
    
    // Handle literal boolean values
    if (path === 'true') return true;
    if (path === 'false') return false;
    
    // Handle numeric literals
    if (!isNaN(Number(path)) && path.trim() !== '') return Number(path);

    // Try to navigate path
    const parts = path.split('.');
    let value = this.context;
    
    for (const part of parts) {
      if (value === undefined || value === null) {
        // Path doesn't exist in context - return original string as literal
        return path;
      }
      value = value[part];
    }
    
    // If we got undefined at the end, return the original string as literal
    if (value === undefined) return path;
    
    return value;
  }

  /**
   * Evaluate a predicate object
   */
  evaluate(predicate) {
    if (predicate === null || predicate === undefined) return false;
    
    // Handle primitive values
    if (typeof predicate === 'boolean') return predicate;
    if (typeof predicate === 'string') return this.resolvePath(predicate);
    
    // Handle predicate objects
    if (typeof predicate !== 'object') return Boolean(predicate);

    // Get the operator (first key)
    const operators = Object.keys(predicate);
    if (operators.length === 0) return true;

    const op = operators[0];
    const args = predicate[op];

    switch (op) {
      // Always true/false
      case 'always':
        return Boolean(args);
      
      // Comparison operators
      case 'eq':
        return this._compareValues(args[0], args[1], (a, b) => a === b);
      case 'neq':
        return this._compareValues(args[0], args[1], (a, b) => a !== b);
      case 'gt':
        return this._compareValues(args[0], args[1], (a, b) => a > b);
      case 'gte':
        return this._compareValues(args[0], args[1], (a, b) => a >= b);
      case 'lt':
        return this._compareValues(args[0], args[1], (a, b) => a < b);
      case 'lte':
        return this._compareValues(args[0], args[1], (a, b) => a <= b);

      // Logical operators
      case 'and':
        return args.every(p => this.evaluate(p));
      case 'or':
        return args.some(p => this.evaluate(p));
      case 'not':
        return !this.evaluate(args);

      // Set operators
      case 'in':
        return this._setContains(args[1], args[0]);
      case 'notIn':
        return !this._setContains(args[1], args[0]);
      case 'isEmpty':
        return this._isEmpty(args);
      case 'hasLength':
        return this._hasLength(args[0], args[1]);

      // Interval operators
      case 'intervalSubset':
        return this._intervalSubset(args[0], args[1]);
      case 'intervalOverlap':
        return this._intervalOverlap(args[0], args[1]);
      case 'pointInInterval':
        return this._pointInInterval(args[0], args[1]);

      // Numeric operators
      case 'between':
        return this._between(args[0], args[1], args[2]);
      case 'approximately':
        return this._approximately(args[0], args[1], args[2] || 0.0001);

      default:
        console.warn(`Unknown predicate operator: ${op}`);
        return false;
    }
  }

  _compareValues(left, right, comparator) {
    const leftVal = this.resolvePath(left);
    const rightVal = this.resolvePath(right);
    
    // Handle object comparison
    if (typeof leftVal === 'object' && typeof rightVal === 'object') {
      return JSON.stringify(leftVal) === JSON.stringify(rightVal);
    }
    
    return comparator(leftVal, rightVal);
  }

  _setContains(set, value) {
    const resolvedSet = this.resolvePath(set);
    const resolvedValue = this.resolvePath(value);
    
    if (Array.isArray(resolvedSet)) {
      return resolvedSet.includes(resolvedValue);
    }
    return false;
  }

  _isEmpty(collection) {
    const resolved = this.resolvePath(collection);
    if (Array.isArray(resolved)) return resolved.length === 0;
    if (resolved instanceof IntervalSet) return resolved.isEmpty();
    return !resolved;
  }

  _hasLength(collection, length) {
    const resolved = this.resolvePath(collection);
    const resolvedLength = this.resolvePath(length);
    if (Array.isArray(resolved)) return resolved.length === resolvedLength;
    return false;
  }

  _intervalSubset(testInterval, referenceIntervals) {
    const test = this.resolvePath(testInterval);
    const reference = this.resolvePath(referenceIntervals);

    // Convert to Interval objects if needed
    const testObj = test instanceof Interval ? test : Interval.fromJSON(test);
    
    // Reference can be single interval or IntervalSet
    if (reference instanceof IntervalSet) {
      return reference.containsInterval(testObj);
    }
    if (Array.isArray(reference)) {
      const refSet = new IntervalSet(reference);
      return refSet.containsInterval(testObj);
    }
    if (reference instanceof Interval) {
      return testObj.isSubsetOf(reference);
    }
    
    return false;
  }

  _intervalOverlap(interval1, interval2) {
    const int1 = this.resolvePath(interval1);
    const int2 = this.resolvePath(interval2);

    const i1 = int1 instanceof Interval ? int1 : Interval.fromJSON(int1);
    const i2 = int2 instanceof Interval ? int2 : Interval.fromJSON(int2);

    return i1.overlaps(i2);
  }

  _pointInInterval(point, interval) {
    const p = this.resolvePath(point);
    const int = this.resolvePath(interval);

    if (int instanceof Interval) return int.contains(p);
    if (int instanceof IntervalSet) return int.contains(p);
    if (Array.isArray(int)) {
      return new IntervalSet(int).contains(p);
    }
    
    return false;
  }

  _between(value, min, max) {
    const v = this.resolvePath(value);
    const minVal = this.resolvePath(min);
    const maxVal = this.resolvePath(max);
    return v >= minVal && v <= maxVal;
  }

  _approximately(value1, value2, tolerance) {
    const v1 = this.resolvePath(value1);
    const v2 = this.resolvePath(value2);
    const tol = this.resolvePath(tolerance);
    return Math.abs(v1 - v2) <= tol;
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 3: GRAPH FEATURE EXTRACTOR
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extract mathematical features from function parameters
 * Supports: Linear, Quadratic, Hyperbola, Exponential
 */
export class GraphFeatureExtractor {
  
  /**
   * Main extraction method
   * @param {string} functionType - 'linear' | 'quadratic' | 'hyperbola' | 'exponential'
   * @param {object} params - Function parameters (a, b, c, p, q, m, etc.)
   * @param {Interval} domain - Domain restriction (optional)
   * @returns {object} Complete feature set
   */
  static extractFeatures(functionType, params, domain = null) {
    const naturalDomain = this.getNaturalDomain(functionType, params);
    const effectiveDomain = domain ? naturalDomain.intersection(domain) : naturalDomain;

    const extractor = {
      'linear': this.extractLinearFeatures,
      'quadratic': this.extractQuadraticFeatures,
      'hyperbola': this.extractHyperbolaFeatures,
      'exponential': this.extractExponentialFeatures,
    }[functionType];

    if (!extractor) {
      throw new Error(`Unknown function type: ${functionType}`);
    }

    const features = extractor.call(this, params, effectiveDomain);
    
    return {
      functionType,
      params,
      naturalDomain: naturalDomain.toJSON(),
      domain: effectiveDomain.toJSON(),
      ...features
    };
  }

  /**
   * Get natural domain for function type
   */
  static getNaturalDomain(functionType, params) {
    switch (functionType) {
      case 'hyperbola':
        // Exclude vertical asymptote x = -p
        // Return as two intervals: (-∞, -p) ∪ (-p, ∞)
        // For simplicity, we'll handle this in feature extraction
        return Interval.all();
      default:
        return Interval.all();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // LINEAR: y = mx + c
  // ─────────────────────────────────────────────────────────────────────────────
  static extractLinearFeatures(params, domain) {
    const { m, c } = params;
    
    // Function definition
    const f = (x) => m * x + c;
    
    // Root calculation
    const roots = [];
    if (m !== 0) {
      const root = -c / m;
      if (domain.contains(root)) {
        roots.push(root);
      }
    }

    // Y-intercept
    const yIntercept = domain.contains(0) ? c : null;

    // X-intercept (same as root for linear)
    const xIntercepts = roots;

    // Sign intervals
    const { positiveIntervals, negativeIntervals, zeroPoints } = 
      this.buildSignChart(f, roots, domain, m > 0 ? 'positive' : 'negative');

    // Monotonicity
    let increasingIntervals = new IntervalSet([]);
    let decreasingIntervals = new IntervalSet([]);
    let constantIntervals = new IntervalSet([]);

    if (m > 0) {
      increasingIntervals = new IntervalSet([domain]);
    } else if (m < 0) {
      decreasingIntervals = new IntervalSet([domain]);
    } else {
      constantIntervals = new IntervalSet([domain]);
    }

    // Range
    const range = this.computeLinearRange(m, c, domain);

    // Equation display
    const equation = this.formatLinearEquation(m, c);

    // Transformations relative to y = x
    const transformations = {
      verticalStretch: Math.abs(m),
      verticalShift: c,
      horizontalShift: 0,
      reflectedX: m < 0,
      reflectedY: false,
    };

    return {
      equation,
      roots,
      rootCount: roots.length,
      yIntercept,
      xIntercepts,
      vertex: null,
      turningPoints: [],
      verticalAsymptotes: [],
      horizontalAsymptote: null,
      opens: null,
      gradient: m,
      positiveIntervals: positiveIntervals.toJSON(),
      negativeIntervals: negativeIntervals.toJSON(),
      zeroPoints,
      increasingIntervals: increasingIntervals.toJSON(),
      decreasingIntervals: decreasingIntervals.toJSON(),
      constantIntervals: constantIntervals.toJSON(),
      range: range.toJSON(),
      isOneToOne: m !== 0,
      inverseExists: m !== 0,
      transformations,
    };
  }

  static computeLinearRange(m, c, domain) {
    if (domain.isEmpty()) return Interval.empty();
    
    if (m === 0) {
      return Interval.point(c);
    }

    const f = (x) => m * x + c;
    
    let minY, maxY;
    if (m > 0) {
      minY = domain.from === -Infinity ? -Infinity : f(domain.from);
      maxY = domain.to === Infinity ? Infinity : f(domain.to);
    } else {
      minY = domain.to === Infinity ? -Infinity : f(domain.to);
      maxY = domain.from === -Infinity ? Infinity : f(domain.from);
    }

    return new Interval(
      minY, maxY,
      isFinite(minY) && domain.fromInclusive,
      isFinite(maxY) && domain.toInclusive
    );
  }

  static formatLinearEquation(m, c) {
    if (m === 0) return `y = ${c}`;
    
    let eq = 'y = ';
    if (m === 1) eq += 'x';
    else if (m === -1) eq += '-x';
    else eq += `${m}x`;
    
    if (c > 0) eq += ` + ${c}`;
    else if (c < 0) eq += ` - ${Math.abs(c)}`;
    
    return eq;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // QUADRATIC: y = a(x - p)² + q (Vertex Form)
  // ─────────────────────────────────────────────────────────────────────────────
  static extractQuadraticFeatures(params, domain) {
    const { a, p, q } = params;
    
    // Function definition
    const f = (x) => a * Math.pow(x - p, 2) + q;

    // Vertex
    const vertex = { x: p, y: q };

    // Root calculation: a(x-p)² + q = 0 → (x-p)² = -q/a
    const roots = [];
    const discriminant = -q / a;
    
    if (discriminant > 0) {
      const sqrtD = Math.sqrt(discriminant);
      const root1 = p - sqrtD;
      const root2 = p + sqrtD;
      if (domain.contains(root1)) roots.push(root1);
      if (domain.contains(root2)) roots.push(root2);
      roots.sort((x, y) => x - y);
    } else if (discriminant === 0) {
      if (domain.contains(p)) roots.push(p);
    }

    // Y-intercept: f(0) = a(0-p)² + q = ap² + q
    const yInterceptValue = a * p * p + q;
    const yIntercept = domain.contains(0) ? yInterceptValue : null;

    // Sign intervals
    const signAtInfinity = a > 0 ? 'positive' : 'negative';
    const { positiveIntervals, negativeIntervals, zeroPoints } = 
      this.buildSignChart(f, roots, domain, signAtInfinity);

    // Monotonicity
    let increasingIntervals, decreasingIntervals;
    
    if (a > 0) {
      // Opens up: decreasing left of vertex, increasing right
      const leftOfVertex = domain.intersection(Interval.open(-Infinity, p));
      const rightOfVertex = domain.intersection(Interval.open(p, Infinity));
      decreasingIntervals = new IntervalSet(leftOfVertex.isEmpty() ? [] : [leftOfVertex]);
      increasingIntervals = new IntervalSet(rightOfVertex.isEmpty() ? [] : [rightOfVertex]);
    } else {
      // Opens down: increasing left of vertex, decreasing right
      const leftOfVertex = domain.intersection(Interval.open(-Infinity, p));
      const rightOfVertex = domain.intersection(Interval.open(p, Infinity));
      increasingIntervals = new IntervalSet(leftOfVertex.isEmpty() ? [] : [leftOfVertex]);
      decreasingIntervals = new IntervalSet(rightOfVertex.isEmpty() ? [] : [rightOfVertex]);
    }

    // Range
    const range = this.computeQuadraticRange(a, p, q, domain);

    // Equation display
    const equation = this.formatQuadraticEquation(a, p, q);

    // Standard form coefficients: y = ax² - 2apx + ap² + q
    const standardForm = {
      a: a,
      b: -2 * a * p,
      c: a * p * p + q
    };

    // Transformations
    const transformations = {
      verticalStretch: Math.abs(a),
      verticalShift: q,
      horizontalShift: p,
      reflectedX: a < 0,
      reflectedY: false,
    };

    // Axis of symmetry
    const axisOfSymmetry = p;

    return {
      equation,
      standardForm,
      roots,
      rootCount: roots.length,
      discriminant,
      yIntercept,
      xIntercepts: roots,
      vertex,
      turningPoints: [vertex],
      axisOfSymmetry,
      verticalAsymptotes: [],
      horizontalAsymptote: null,
      opens: a > 0 ? 'up' : 'down',
      concavity: a > 0 ? 'concave_up' : 'concave_down',
      positiveIntervals: positiveIntervals.toJSON(),
      negativeIntervals: negativeIntervals.toJSON(),
      zeroPoints,
      increasingIntervals: increasingIntervals.toJSON(),
      decreasingIntervals: decreasingIntervals.toJSON(),
      constantIntervals: [],
      range: range.toJSON(),
      extremum: {
        type: a > 0 ? 'minimum' : 'maximum',
        value: q,
        location: p
      },
      isOneToOne: false,
      inverseExists: false, // Unless domain restricted
      transformations,
    };
  }

  static computeQuadraticRange(a, p, q, domain) {
    if (domain.isEmpty()) return Interval.empty();

    const f = (x) => a * Math.pow(x - p, 2) + q;
    
    // Check if vertex is in domain
    const vertexInDomain = domain.contains(p);
    
    // Evaluate at domain boundaries
    const boundaryValues = [];
    if (isFinite(domain.from)) boundaryValues.push(f(domain.from));
    if (isFinite(domain.to)) boundaryValues.push(f(domain.to));
    if (vertexInDomain) boundaryValues.push(q);

    if (a > 0) {
      // Opens up: min at vertex (if in domain), max at boundaries
      const minY = vertexInDomain ? q : Math.min(...boundaryValues);
      let maxY;
      if (!isFinite(domain.from) || !isFinite(domain.to)) {
        maxY = Infinity;
      } else {
        maxY = Math.max(...boundaryValues);
      }
      return new Interval(minY, maxY, true, isFinite(maxY));
    } else {
      // Opens down: max at vertex (if in domain), min at boundaries
      const maxY = vertexInDomain ? q : Math.max(...boundaryValues);
      let minY;
      if (!isFinite(domain.from) || !isFinite(domain.to)) {
        minY = -Infinity;
      } else {
        minY = Math.min(...boundaryValues);
      }
      return new Interval(minY, maxY, isFinite(minY), true);
    }
  }

  static formatQuadraticEquation(a, p, q) {
    let eq = 'y = ';
    
    // Coefficient
    if (a === 1) eq += '';
    else if (a === -1) eq += '-';
    else eq += `${a}`;
    
    // (x - p)² part
    if (p === 0) {
      eq += 'x²';
    } else if (p > 0) {
      eq += `(x - ${p})²`;
    } else {
      eq += `(x + ${Math.abs(p)})²`;
    }
    
    // + q part
    if (q > 0) eq += ` + ${q}`;
    else if (q < 0) eq += ` - ${Math.abs(q)}`;
    
    return eq;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // HYPERBOLA: y = a/(x + p) + q
  // ─────────────────────────────────────────────────────────────────────────────
  static extractHyperbolaFeatures(params, domain) {
    const { a, p, q } = params;
    
    // Function definition (undefined at x = -p)
    const f = (x) => {
      if (x === -p) return NaN;
      return a / (x + p) + q;
    };

    // Asymptotes
    const verticalAsymptote = -p;
    const horizontalAsymptote = q;

    // Root: a/(x+p) + q = 0 → x = -a/q - p
    const roots = [];
    if (q !== 0) {
      const root = -a / q - p;
      if (root !== verticalAsymptote && domain.contains(root)) {
        roots.push(root);
      }
    }

    // Y-intercept: f(0) = a/p + q (if p ≠ 0 and 0 in domain)
    let yIntercept = null;
    if (p !== 0 && domain.contains(0)) {
      yIntercept = a / p + q;
    }

    // Split domain at asymptote
    const leftDomain = domain.intersection(Interval.open(-Infinity, verticalAsymptote));
    const rightDomain = domain.intersection(Interval.open(verticalAsymptote, Infinity));

    // Sign analysis (more complex due to asymptote)
    const { positiveIntervals, negativeIntervals, zeroPoints } = 
      this.buildHyperbolaSignChart(a, p, q, roots, leftDomain, rightDomain);

    // Monotonicity: derivative is -a/(x+p)²
    // If a > 0: always decreasing on each branch
    // If a < 0: always increasing on each branch
    let increasingIntervals, decreasingIntervals;
    
    const branches = [];
    if (!leftDomain.isEmpty()) branches.push(leftDomain);
    if (!rightDomain.isEmpty()) branches.push(rightDomain);
    
    if (a > 0) {
      decreasingIntervals = new IntervalSet(branches);
      increasingIntervals = new IntervalSet([]);
    } else {
      increasingIntervals = new IntervalSet(branches);
      decreasingIntervals = new IntervalSet([]);
    }

    // Range: y ∈ (-∞, q) ∪ (q, ∞) for full domain
    const range = this.computeHyperbolaRange(a, p, q, domain);

    // Equation display
    const equation = this.formatHyperbolaEquation(a, p, q);

    // Transformations
    const transformations = {
      verticalStretch: Math.abs(a),
      verticalShift: q,
      horizontalShift: -p,
      reflectedX: a < 0,
      reflectedY: false,
    };

    // Quadrants (for a > 0: quadrants 1 & 3 relative to asymptotes; for a < 0: 2 & 4)
    const quadrants = a > 0 ? [1, 3] : [2, 4];

    return {
      equation,
      roots,
      rootCount: roots.length,
      yIntercept,
      xIntercepts: roots,
      vertex: null,
      turningPoints: [],
      verticalAsymptotes: [verticalAsymptote],
      horizontalAsymptote,
      asymptoteIntersection: { x: verticalAsymptote, y: horizontalAsymptote },
      opens: null,
      quadrants,
      positiveIntervals: positiveIntervals.toJSON(),
      negativeIntervals: negativeIntervals.toJSON(),
      zeroPoints,
      increasingIntervals: increasingIntervals.toJSON(),
      decreasingIntervals: decreasingIntervals.toJSON(),
      constantIntervals: [],
      range: range.toJSON(),
      isOneToOne: true,
      inverseExists: true,
      transformations,
    };
  }

  static buildHyperbolaSignChart(a, p, q, roots, leftDomain, rightDomain) {
    const f = (x) => a / (x + p) + q;
    const positiveIntervals = [];
    const negativeIntervals = [];
    const zeroPoints = [...roots];

    // Helper to check sign at a test point
    const checkSign = (testPoint) => {
      const val = f(testPoint);
      return val > 0 ? 'positive' : val < 0 ? 'negative' : 'zero';
    };

    // Process left branch
    if (!leftDomain.isEmpty()) {
      const testPoint = leftDomain.midpoint();
      const sign = checkSign(testPoint);
      
      if (roots.length > 0 && leftDomain.contains(roots[0])) {
        const root = roots[0];
        const beforeRoot = leftDomain.intersection(Interval.open(leftDomain.from, root));
        const afterRoot = leftDomain.intersection(Interval.open(root, leftDomain.to));
        
        if (!beforeRoot.isEmpty()) {
          if (checkSign(beforeRoot.midpoint()) === 'positive') positiveIntervals.push(beforeRoot);
          else negativeIntervals.push(beforeRoot);
        }
        if (!afterRoot.isEmpty()) {
          if (checkSign(afterRoot.midpoint()) === 'positive') positiveIntervals.push(afterRoot);
          else negativeIntervals.push(afterRoot);
        }
      } else {
        if (sign === 'positive') positiveIntervals.push(leftDomain);
        else if (sign === 'negative') negativeIntervals.push(leftDomain);
      }
    }

    // Process right branch
    if (!rightDomain.isEmpty()) {
      const testPoint = rightDomain.midpoint();
      const sign = checkSign(testPoint);
      
      if (roots.length > 0 && rightDomain.contains(roots[0])) {
        const root = roots[0];
        const beforeRoot = rightDomain.intersection(Interval.open(rightDomain.from, root));
        const afterRoot = rightDomain.intersection(Interval.open(root, rightDomain.to));
        
        if (!beforeRoot.isEmpty()) {
          if (checkSign(beforeRoot.midpoint()) === 'positive') positiveIntervals.push(beforeRoot);
          else negativeIntervals.push(beforeRoot);
        }
        if (!afterRoot.isEmpty()) {
          if (checkSign(afterRoot.midpoint()) === 'positive') positiveIntervals.push(afterRoot);
          else negativeIntervals.push(afterRoot);
        }
      } else {
        if (sign === 'positive') positiveIntervals.push(rightDomain);
        else if (sign === 'negative') negativeIntervals.push(rightDomain);
      }
    }

    return {
      positiveIntervals: new IntervalSet(positiveIntervals),
      negativeIntervals: new IntervalSet(negativeIntervals),
      zeroPoints
    };
  }

  static computeHyperbolaRange(a, p, q, domain) {
    // For full domain, range is (-∞, q) ∪ (q, ∞)
    // For restricted domain, need to compute boundary values
    
    if (!isFinite(domain.from) && !isFinite(domain.to)) {
      // Full domain (excluding asymptote): range excludes q
      return Interval.all(); // Simplified; true range is R \ {q}
    }

    const f = (x) => a / (x + p) + q;
    const verticalAsymptote = -p;
    
    const values = [];
    if (isFinite(domain.from) && domain.from !== verticalAsymptote) {
      values.push(f(domain.from));
    }
    if (isFinite(domain.to) && domain.to !== verticalAsymptote) {
      values.push(f(domain.to));
    }

    // Check limits approaching asymptote
    if (domain.contains(verticalAsymptote - 0.0001) || domain.contains(verticalAsymptote + 0.0001)) {
      if (a > 0) {
        values.push(Infinity, -Infinity);
      } else {
        values.push(-Infinity, Infinity);
      }
    }

    if (values.length === 0) return Interval.empty();

    const minY = Math.min(...values);
    const maxY = Math.max(...values);
    
    return new Interval(minY, maxY, isFinite(minY), isFinite(maxY));
  }

  static formatHyperbolaEquation(a, p, q) {
    let eq = 'y = ';
    
    // a/(x+p) part
    if (a === 1) eq += '1';
    else if (a === -1) eq += '-1';
    else eq += `${a}`;
    
    if (p === 0) {
      eq += '/x';
    } else if (p > 0) {
      eq += `/(x + ${p})`;
    } else {
      eq += `/(x - ${Math.abs(p)})`;
    }
    
    // + q part
    if (q > 0) eq += ` + ${q}`;
    else if (q < 0) eq += ` - ${Math.abs(q)}`;
    
    return eq;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // EXPONENTIAL: y = a·b^(x+p) + q
  // ─────────────────────────────────────────────────────────────────────────────
  static extractExponentialFeatures(params, domain) {
    const { a, b, p, q } = params;
    
    // Validate base
    if (b <= 0 || b === 1) {
      throw new Error('Exponential base b must be positive and not equal to 1');
    }

    // Function definition
    const f = (x) => a * Math.pow(b, x + p) + q;

    // Horizontal asymptote
    const horizontalAsymptote = q;

    // Root: a·b^(x+p) + q = 0 → b^(x+p) = -q/a
    const roots = [];
    const target = -q / a;
    if (target > 0) {
      const root = Math.log(target) / Math.log(b) - p;
      if (domain.contains(root)) {
        roots.push(root);
      }
    }

    // Y-intercept: f(0) = a·b^p + q
    const yInterceptValue = a * Math.pow(b, p) + q;
    const yIntercept = domain.contains(0) ? yInterceptValue : null;

    // Sign analysis
    const { positiveIntervals, negativeIntervals, zeroPoints } = 
      this.buildExponentialSignChart(a, b, p, q, roots, domain);

    // Monotonicity: derivative has same sign as a·ln(b)
    const derivativeSign = a * Math.log(b);
    let increasingIntervals, decreasingIntervals;
    
    if (derivativeSign > 0) {
      increasingIntervals = new IntervalSet([domain]);
      decreasingIntervals = new IntervalSet([]);
    } else {
      decreasingIntervals = new IntervalSet([domain]);
      increasingIntervals = new IntervalSet([]);
    }

    // Range
    const range = this.computeExponentialRange(a, b, p, q, domain);

    // Equation display
    const equation = this.formatExponentialEquation(a, b, p, q);

    // Transformations
    const transformations = {
      verticalStretch: Math.abs(a),
      verticalShift: q,
      horizontalShift: -p,
      reflectedX: a < 0,
      reflectedY: false,
      base: b,
    };

    // Growth/Decay classification
    let behavior;
    if (a > 0 && b > 1) behavior = 'growth';
    else if (a > 0 && b < 1) behavior = 'decay';
    else if (a < 0 && b > 1) behavior = 'reflected_growth';
    else behavior = 'reflected_decay';

    return {
      equation,
      roots,
      rootCount: roots.length,
      yIntercept,
      xIntercepts: roots,
      vertex: null,
      turningPoints: [],
      verticalAsymptotes: [],
      horizontalAsymptote,
      opens: null,
      behavior,
      base: b,
      positiveIntervals: positiveIntervals.toJSON(),
      negativeIntervals: negativeIntervals.toJSON(),
      zeroPoints,
      increasingIntervals: increasingIntervals.toJSON(),
      decreasingIntervals: decreasingIntervals.toJSON(),
      constantIntervals: [],
      range: range.toJSON(),
      isOneToOne: true,
      inverseExists: true,
      transformations,
    };
  }

  static buildExponentialSignChart(a, b, p, q, roots, domain) {
    const f = (x) => a * Math.pow(b, x + p) + q;
    
    // Test sign at midpoint or accessible point
    const testPoint = domain.midpoint();
    const testValue = f(testPoint);

    let positiveIntervals = new IntervalSet([]);
    let negativeIntervals = new IntervalSet([]);
    const zeroPoints = [...roots];

    if (roots.length === 0) {
      // No roots: entire domain has same sign
      if (testValue > 0) {
        positiveIntervals = new IntervalSet([domain]);
      } else if (testValue < 0) {
        negativeIntervals = new IntervalSet([domain]);
      }
    } else {
      // One root: sign changes at root
      const root = roots[0];
      const leftOfRoot = domain.intersection(Interval.open(domain.from, root));
      const rightOfRoot = domain.intersection(Interval.open(root, domain.to));
      
      // Test each region
      if (!leftOfRoot.isEmpty()) {
        const leftTest = f(leftOfRoot.midpoint());
        if (leftTest > 0) positiveIntervals = positiveIntervals.union(new IntervalSet([leftOfRoot]));
        else if (leftTest < 0) negativeIntervals = negativeIntervals.union(new IntervalSet([leftOfRoot]));
      }
      
      if (!rightOfRoot.isEmpty()) {
        const rightTest = f(rightOfRoot.midpoint());
        if (rightTest > 0) positiveIntervals = positiveIntervals.union(new IntervalSet([rightOfRoot]));
        else if (rightTest < 0) negativeIntervals = negativeIntervals.union(new IntervalSet([rightOfRoot]));
      }
    }

    return { positiveIntervals, negativeIntervals, zeroPoints };
  }

  static computeExponentialRange(a, b, p, q, domain) {
    const f = (x) => a * Math.pow(b, x + p) + q;
    
    // Exponential is monotonic, so range depends on:
    // - Direction (a·ln(b) sign)
    // - Horizontal asymptote q
    // - Domain boundaries

    const isIncreasing = a * Math.log(b) > 0;
    
    const values = [];
    if (isFinite(domain.from)) values.push(f(domain.from));
    if (isFinite(domain.to)) values.push(f(domain.to));

    // Limits at infinity
    if (!isFinite(domain.from)) {
      // x → -∞
      if (a > 0) values.push(q); // approaches asymptote from above
      else values.push(q); // approaches asymptote from below
    }
    if (!isFinite(domain.to)) {
      // x → +∞
      if (isIncreasing) values.push(Infinity);
      else values.push(-Infinity);
    }
    if (!isFinite(domain.from)) {
      if (!isIncreasing) values.push(Infinity);
      else values.push(-Infinity);
    }

    // Determine range based on asymptote
    if (a > 0) {
      // Graph above asymptote
      return new Interval(q, Infinity, false, false);
    } else {
      // Graph below asymptote
      return new Interval(-Infinity, q, false, false);
    }
  }

  static formatExponentialEquation(a, b, p, q) {
    let eq = 'y = ';
    
    // Coefficient
    if (a === 1) eq += '';
    else if (a === -1) eq += '-';
    else eq += `${a}·`;
    
    // Base and exponent
    eq += `${b}`;
    if (p === 0) {
      eq += '^x';
    } else if (p > 0) {
      eq += `^(x + ${p})`;
    } else {
      eq += `^(x - ${Math.abs(p)})`;
    }
    
    // + q part
    if (q > 0) eq += ` + ${q}`;
    else if (q < 0) eq += ` - ${Math.abs(q)}`;
    
    return eq;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // GENERIC SIGN CHART BUILDER
  // ─────────────────────────────────────────────────────────────────────────────
  static buildSignChart(f, roots, domain, signAtPositiveInfinity) {
    const sortedRoots = [...roots].sort((a, b) => a - b);
    const positiveIntervals = [];
    const negativeIntervals = [];
    const zeroPoints = [...sortedRoots];

    // Build intervals from domain and roots
    const criticalPoints = [domain.from, ...sortedRoots, domain.to];
    
    for (let i = 0; i < criticalPoints.length - 1; i++) {
      const left = criticalPoints[i];
      const right = criticalPoints[i + 1];
      
      if (left >= right) continue;
      
      // Determine boundary inclusion
      const leftInclusive = (i === 0) ? domain.fromInclusive : false;
      const rightInclusive = (i === criticalPoints.length - 2) ? domain.toInclusive : false;
      
      const interval = new Interval(left, right, leftInclusive, rightInclusive);
      if (interval.isEmpty()) continue;

      // Test sign at midpoint
      const testPoint = interval.midpoint();
      const value = f(testPoint);
      
      if (value > 0) {
        positiveIntervals.push(interval);
      } else if (value < 0) {
        negativeIntervals.push(interval);
      }
    }

    return {
      positiveIntervals: new IntervalSet(positiveIntervals),
      negativeIntervals: new IntervalSet(negativeIntervals),
      zeroPoints
    };
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Round to specified decimal places
 */
export function roundTo(value, decimals = 4) {
  if (!isFinite(value)) return value;
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

/**
 * Format number for display (handles special values)
 */
export function formatNumber(value, decimals = 2) {
  if (value === Infinity) return '∞';
  if (value === -Infinity) return '-∞';
  if (isNaN(value)) return 'undefined';
  
  // Check if value is close to a nice fraction
  const niceFractions = [
    { val: Math.sqrt(2), str: '√2' },
    { val: Math.sqrt(3), str: '√3' },
    { val: Math.PI, str: 'π' },
  ];
  
  for (const { val, str } of niceFractions) {
    if (Math.abs(value - val) < 0.0001) return str;
    if (Math.abs(value + val) < 0.0001) return `-${str}`;
  }
  
  // Regular number
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(decimals).replace(/\.?0+$/, '');
}

/**
 * Get nested value from object using dot notation path
 */
export function getNestedValue(obj, path) {
  if (!path) return obj;
  const parts = path.split('.');
  let value = obj;
  for (const part of parts) {
    if (value === undefined || value === null) return undefined;
    value = value[part];
  }
  return value;
}

/**
 * Seeded random number generator for reproducibility
 */
export class SeededRandom {
  constructor(seed) {
    this.seed = seed % 2147483647;
    if (this.seed <= 0) this.seed += 2147483646;
  }

  next() {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }

  nextInt(min, max) {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  choice(array) {
    return array[this.nextInt(0, array.length - 1)];
  }

  shuffle(array) {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i);
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export const dynamicQuestionService = {
  // Interval operations
  Interval,
  IntervalSet,
  
  // Predicate evaluation
  PredicateEvaluator,
  
  // Feature extraction
  GraphFeatureExtractor,
  extractFeatures: (functionType, params, domain) => 
    GraphFeatureExtractor.extractFeatures(functionType, params, domain),
  
  // Utilities
  roundTo,
  formatNumber,
  getNestedValue,
  SeededRandom,
  
  // Convenience: Create evaluator and evaluate in one step
  evaluatePredicate: (predicate, context) => {
    const evaluator = new PredicateEvaluator(context);
    return evaluator.evaluate(predicate);
  },
};

export default dynamicQuestionService;